<script lang="ts" setup>
import { Modal } from "@/components";
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { useUpdateWorkspaceNameMutation } from "@/graphql/operations";

const props = defineProps<{ active: boolean; name: string; id: string }>();
const emit = defineEmits(["close", "success"]);
const name = ref("");
const input = ref<HTMLInputElement>();
const submitting = ref(false);
const failed = ref(false);
const trimmedName = computed(() => name.value.trim());
let unmounted = false;
onBeforeUnmount(() => {
  unmounted = true;
});
const close = () => {
  if (submitting.value) return;
  name.value = "";
  failed.value = false;
  emit("close");
};
watch(
  () => props.active,
  async (active) => {
    if (!active) return;
    name.value = props.name;
    failed.value = false;
    await nextTick();
    if (props.active && !unmounted) input.value?.focus();
  },
);
const { executeMutation } = useUpdateWorkspaceNameMutation();
const submit = async () => {
  if (!trimmedName.value || submitting.value) return;
  submitting.value = true;
  failed.value = false;
  try {
    const result = await executeMutation({
      id: props.id,
      name: trimmedName.value,
    });
    if (unmounted) return;
    if (result.error) {
      failed.value = true;
      return;
    }
    emit("close");
    emit("success");
  } catch {
    if (!unmounted) failed.value = true;
  } finally {
    submitting.value = false;
  }
};
</script>

<template>
  <Modal @close="close" :active="active" :close-disabled="submitting">
    <template #header>{{ $t("workspaceDetail.rename.modalHeader") }}</template>
    <template #body>
      <input
        type="text"
        class="input"
        ref="input"
        v-model="name"
        :disabled="submitting"
      />
      <p v-if="failed" role="alert" class="has-text-danger">
        {{ $t("error") }}
      </p>
    </template>
    <template #footer>
      <div class="buttons">
        <button
          class="button primary"
          :class="{ disabled: !trimmedName || submitting }"
          :disabled="!trimmedName || submitting"
          @click="submit"
        >
          {{ $t("submit") }}
        </button>
        <button class="button secondary" :disabled="submitting" @click="close">
          {{ $t("cancel") }}
        </button>
      </div>
    </template>
  </Modal>
</template>
